import { createFileRoute, Link } from '@tanstack/react-router';
import { Shield, Settings, Cpu, Globe, Wrench, FileText, Phone, Lock, Store, Mail, Network } from 'lucide-react';
import styles from './_auth.settings.module.css';
import { PageGuide } from '@/components/PageGuide';


export const Route = createFileRoute('/_auth/settings/')({
  component: SettingsPage,
});

export default function SettingsPage() {
  return (
    <div className={styles.page}>
      <PageGuide pageKey="settings" />
      <div className={styles.header}>
        <h1 className={styles.title}><Settings size={24} /> Settings</h1>
        <p className={styles.subtitle}>Configure your tenant settings, integrations, and developer tools</p>
      </div>

      <h2 className={styles.sectionTitle}>API Access</h2>
      <div className={styles.grid}>
        <Link to="/settings/api-keys" className={styles.card}>
          <div className={styles.cardIcon}><Shield size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>API Keys &amp; Chat Apps</h3>
            <p className={styles.cardDesc}>Manage platform API keys with scoped access and Chat App keys for the EnskaChat SDK</p>
          </div>
        </Link>
        <Link to="/settings/llm-providers" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(16,185,129,0.12)', color: 'var(--color-success)' }}><Cpu size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>LLM Providers</h3>
            <p className={styles.cardDesc}>Add per-tenant API keys for Gemini, OpenAI, and Anthropic. Overrides platform defaults.</p>
          </div>
        </Link>
      </div>

      <h2 className={styles.sectionTitle}>Compliance &amp; Security</h2>
      <div className={styles.grid}>
        <Link to="/settings/audit-log" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(239,68,68,0.10)', color: '#f87171' }}><FileText size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>Audit Log</h3>
            <p className={styles.cardDesc}>Every mutation logged — who changed what, when, from which IP. Export for SOC2/ISO27001 compliance.</p>
          </div>
        </Link>
        <Link to="/settings/secrets" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(99,102,241,0.10)', color: '#818cf8' }}><Lock size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>Secret Manager</h3>
            <p className={styles.cardDesc}>Encrypted secrets for tools — API keys, tokens, credentials. Referenced as <code>{'{{'}&thinsp;secrets.NAME&thinsp;{'}}'}</code> in tool configs.</p>
          </div>
        </Link>
      </div>

      <h2 className={styles.sectionTitle}>Integrations &amp; Tools</h2>
      <div className={styles.grid}>
        <Link to="/settings/tools-catalog" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}><Store size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>Tool Catalog</h3>
            <p className={styles.cardDesc}>Pre-built integrations — Resend, Twilio, Stripe, Slack — activate with one click. API keys stored securely in secrets.</p>
          </div>
        </Link>
        <Link to="/settings/integrations" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}><Globe size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>Integrations</h3>
            <p className={styles.cardDesc}>Connect external APIs. Define base URL, auth type (Bearer, API key, Basic), and timeout. Tools are built on top of integrations.</p>
          </div>
        </Link>
        <Link to="/settings/tools" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(99,102,241,0.12)', color: 'var(--accent)' }}><Wrench size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>Tools</h3>
            <p className={styles.cardDesc}>
              <strong>HTTP tools</strong> call external REST APIs via an integration.<br />
              <strong>Code tools</strong> run sandboxed JavaScript with a 5s timeout.
            </p>
          </div>
        </Link>
        <Link to="/event-connectors" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8' }}><Network size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>Event Connectors</h3>
            <p className={styles.cardDesc}>Bidirectional event bridges — Google Pub/Sub, AWS SQS/SNS, EventBridge, Azure Service Bus, Kafka, and Webhooks.</p>
          </div>
        </Link>
      </div>

      <h2 className={styles.sectionTitle}>Communication</h2>
      <div className={styles.grid}>
        <Link to="/settings/voice" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}><Phone size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>Voice Numbers</h3>
            <p className={styles.cardDesc}>Manage phone numbers for voice agents — Twilio integration, number provisioning, and call routing.</p>
          </div>
        </Link>
        <Link to="/settings/email-connectors" className={styles.card}>
          <div className={styles.cardIcon} style={{ background: 'rgba(234,67,53,0.12)', color: '#EA4335' }}><Mail size={22} /></div>
          <div>
            <h3 className={styles.cardTitle}>Email Connectors</h3>
            <p className={styles.cardDesc}>Receive inbound emails from Gmail, Outlook, or any IMAP mailbox. Push-based — no polling.</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
